import React from "react";
import styled from "styled-components";
import { useLanguage } from "../contexts/language";
import { useRoom } from "../contexts/room";
import { LanguageSwitcher } from "./language-switcher";
import { unmodule } from "../util";

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  width: 100%;
  max-width: 448px;
  border-radius: 16px;
  background-color: #fff;
  padding: 24px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
`;

const LanguageSwitcherWrapper = styled.div`
  margin-bottom: 16px;
  display: flex;
  justify-content: flex-end;
`;

const ModalTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const ModalDescription = styled.p`
  margin-top: 8px;
  font-size: 14px;
  color: #666;
`;

const Logo = styled.img`
  height: 44px;
  width: auto;
  margin-bottom: 12px;
`;

const Form = styled.form`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormGroup = styled.div``;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #333;
`;

const Input = styled.input`
  margin-top: 8px;
  width: calc(100% - 32px);
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  background-color: #f9f9f9;
  padding: 12px 16px;
  color: #333;
  font-size: 14px;
  outline: none;
  transition: all 0.2s;

  &::placeholder {
    color: #999;
  }

  &:focus {
    border-color: #c9a961;
    box-shadow: 0 0 0 1px #c9a961;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  border-radius: 8px;
  background-color: #fee;
  padding: 12px;
  font-size: 14px;
  color: #c00;
`;

const SubmitButton = styled.button`
  width: 100%;
  border-radius: 8px;
  background-color: #c9a961;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  border: none;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: rgba(201, 169, 97, 0.8);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const XenditTesterButton = styled.button`
  width: 100%;
  border-radius: 8px;
  background-color: transparent;
  padding: 10px 24px;
  font-size: 13px;
  font-weight: 500;
  color: #888;
  border: 1px dashed #ccc;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: #f9f9f9;
    border-color: #999;
    color: #666;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const RoomInputModal = ({ onRoomVerified, buildingCode }) => {
  const { t } = useLanguage();
  const { setGuestProfile, setTestGuestProfile, isLoading, error, guestName, roomNumber } = useRoom();
  const [nameValue, setNameValue] = React.useState(guestName || "");
  const [roomValue, setRoomValue] = React.useState(roomNumber || "");
  const [localError, setLocalError] = React.useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    setLocalError(null);

    if (!nameValue.trim() && !roomValue.trim()) {
      setLocalError(t.room.error);
      return;
    }

    const success = await setGuestProfile({
      name: nameValue.trim(),
      room: roomValue.trim(),
      buildingCode,
    });
    if (success) {
      onRoomVerified();
    } else {
      setLocalError(t.room.error);
    }
  };

  return (
    <ModalOverlay>
      <ModalContent>
        <LanguageSwitcherWrapper>
          <LanguageSwitcher />
        </LanguageSwitcherWrapper>
        <Logo src={unmodule(require("../assets/logo-dark.png"))} alt="HEBE" />

        <ModalTitle>{t.room.title}</ModalTitle>
        <ModalDescription>{t.room.modalHint}</ModalDescription>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="room-input">{t.room.label}</Label>
            <Input
              id="room-input"
              type="text"
              value={roomValue}
              onChange={(e) => {
                setRoomValue(e.target.value);
                setLocalError(null);
              }}
              placeholder={t.room.placeholder}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="guest-name-input">{t.room.nameLabel}</Label>
            <div style={{ fontSize: "12px", color: "#8a8a8a", marginTop: "4px" }}>
              {t.room.nonGuestHint}
            </div>
            <Input
              id="guest-name-input"
              type="text"
              value={nameValue}
              onChange={(e) => {
                setNameValue(e.target.value);
                setLocalError(null);
              }}
              placeholder={t.room.namePlaceholder}
            />
          </FormGroup>

          {(localError || error) && (
            <ErrorMessage>{localError || error}</ErrorMessage>
          )}

          <SubmitButton type="submit" disabled={isLoading}>
            {isLoading ? t.room.loading : t.room.submit}
          </SubmitButton>

          <XenditTesterButton
            type="button"
            disabled={isLoading}
            onClick={() => {
              setTestGuestProfile(buildingCode);
              onRoomVerified();
            }}
          >
            {t.room.xenditTester}
          </XenditTesterButton>
        </Form>
      </ModalContent>
    </ModalOverlay>
  );
};
